<script lang="ts">
	import dayjs from 'dayjs';
	import type { HDScheduleResponseRow, HDScheduleStream } from '../types/responses/Schedule.js';
	import type { Season } from '#lib/schema-extension.js';
	import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
	import { faExternalLinkAlt } from '@fortawesome/free-solid-svg-icons';
	import { GameStatus } from '../types/util/GameStatus.js';
	import SvgLoader from '#lib/components/SvgLoader.svelte';
	import Placeholder from '#lib/components/Placeholder.svelte';
	import { getNextMatch } from '../hockeydata.remote.js';

	interface CustomGameData {
		homeTeamShortName: string;
		homeTeamLongName: string;
		homeTeamLogo: string;
		awayTeamShortName: string;
		awayTeamLongName: string;
		awayTeamLogo: string;
		scheduledDate: {
			sortValue: number | string;
		};
		scheduledTime: string;
		location: {
			longname: string;
		};
	}

	interface HDNextMatchProps {
		season?: Season;
		customGameData?: CustomGameData;
	}

	let { season, customGameData }: HDNextMatchProps = $props();

	let game = $derived.by(() => {
		if (customGameData) {
			return Promise.resolve(customGameData);
		}
		if (!season) {
			return Promise.reject('No season provided');
		}
		return getNextMatch(season);
	});

	function isCustomGame(game: HDScheduleResponseRow | CustomGameData): game is CustomGameData {
		return !!customGameData;
	}
</script>

<div
	class="bg-base-100 relative isolate flex min-h-87.5 w-full min-w-0 flex-col gap-6 overflow-hidden p-4 sm:p-6 lg:flex-row"
>
	{#await game}
		<Placeholder text="Spiel wird geladen" />
	{:then game}
		{#if !game}
			Kein kommendes Spiel gefunden
		{:else}
			<div class="flex w-full min-w-0 flex-col lg:w-4/12">
				<div class="z-20 flex w-full flex-col">
					<div
						class="text-shadow shadow-primary text-base-100 text-5xl font-bold tracking-wider uppercase"
					>
						Next
					</div>
					<div class="text-5xl font-bold tracking-wider uppercase">Match</div>
				</div>
				<div class="border-accent mt-5 flex w-full flex-col border-l-14">
					<div class="pl-3 text-2xl font-bold">
						{dayjs(game.scheduledDate.sortValue).format('dddd')},
					</div>
					<div class="pl-3 text-3xl font-bold uppercase">
						{dayjs(game.scheduledDate.sortValue).format('DD.MM.YYYY')}
					</div>
					<div
						class="text-shadow shadow-primary text-base-100 pl-3 text-4xl font-bold tracking-wider uppercase"
					>
						{game.scheduledTime}
					</div>
					<div class="mt-3 pl-3 text-2xl font-bold">
						{game.location.longname}
					</div>
				</div>
				<div class="mt-4 flex flex-wrap items-center justify-start gap-2">
					{#if !isCustomGame(game) && game.streams && game.streams.length > 0}
						{#each game.streams as stream}
							{@const streamData = JSON.parse(stream) as HDScheduleStream}
							<a
								href={streamData.targetUrl}
								target={streamData.target}
								rel="noopener noreferrer"
								class="text-base-content hover:bg-base-200 inline-flex min-h-11 items-center gap-1 border-2 border-red-500/50 px-2 text-sm font-semibold uppercase hover:bg-red-500/50"
								onclick={(e) => {
									e.stopPropagation();
								}}
							>
								{streamData.label || 'Livestream'}
								<FontAwesomeIcon icon={faExternalLinkAlt} class="ml-1 h-3 w-3" />
							</a>
						{/each}
					{/if}
					{#if !isCustomGame(game) && game.seasonPhase.includes('PO')}
						<span
							class="text-primary ml-2 w-[12ch] border-2 border-yellow-500/50 px-1 text-center text-sm font-semibold uppercase"
						>
							PLAYOFFS
						</span>
					{/if}
					{#if !isCustomGame(game) && game.gameStatus === GameStatus.PLANNED}
						<span
							class="text-primary ml-2 w-[12ch] border-2 border-blue-500/50 px-2 py-1 text-sm font-semibold uppercase"
						>
							PLANNED
						</span>
					{:else if !isCustomGame(game) && game.gameStatus === GameStatus.LIVE}
						<span
							class="text-primary ml-2 w-[12ch] border-2 border-red-500/50 px-2 py-1 text-sm font-semibold uppercase"
						>
							{game.liveTimeString}
						</span>
					{:else if !isCustomGame(game) && game.gameStatus >= GameStatus.ENDED}
						<span
							class="text-primary ml-2 w-[12ch] border-2 border-green-500/50 px-1 text-center text-sm font-semibold uppercase"
						>
							END{game.isOvertime ? ' OT' : ''}
							{game.isShootOut ? ' PS' : ''}
						</span>
					{/if}
				</div>
			</div>
			<div
				class="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-3 lg:w-8/12"
			>
				<div class="flex min-w-0 flex-col items-center gap-1 text-center text-sm sm:text-base">
					<img
						src={isCustomGame(game)
							? game.homeTeamLogo
							: `https://api.hockeydata.net/img/icehockey/ebel/team-logos/${season?.seasonId}/${game.homeTeamId}.png`}
						alt={game.homeTeamShortName}
						class="mb-1 aspect-square w-full max-w-48 object-contain"
					/>
					<span>{game?.homeTeamShortName}</span>
					<span class="text-center font-bold wrap-anywhere">{game?.homeTeamLongName}</span>
				</div>
				<span class="text-2xl font-bold sm:text-4xl">vs</span>
				<div class="flex min-w-0 flex-col items-center gap-1 text-center text-sm sm:text-base">
					<img
						src={isCustomGame(game)
							? game.awayTeamLogo
							: `https://api.hockeydata.net/img/icehockey/ebel/team-logos/${season?.seasonId}/${game.awayTeamId}.png`}
						alt={game.awayTeamShortName}
						class="mb-1 aspect-square w-full max-w-48 object-contain"
					/>
					<span>{game?.awayTeamShortName}</span>
					<span class="text-center font-bold wrap-anywhere">{game?.awayTeamLongName}</span>
				</div>
			</div>
			<div
				aria-hidden="true"
				class="pointer-events-none absolute top-3 right-0 -z-10 translate-x-1/3 opacity-30"
			>
				<SvgLoader name="stripes" styleClass="fill-accent h-20 w-auto object-cover" />
			</div>
		{/if}
	{:catch error}
		{error}
	{/await}
</div>
